import {
  GridColDef,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid-premium";

import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import { formatProductHierarchy } from "@/Utils/formatProductHierarchy";
import { formatAdStockValue, formatPercentValue, formatUnitValue } from "@/Utils/formatters";
import { Layer } from "@/Utils/hierarchy";
import { Config, NO_SYMBOL } from "@/hooks/useConfig";

import { FunnelStage } from "./filters";
import { AggregatedData } from "./types";
import { FormatCurrencyOptions } from "@/hooks/useConfig/types";

type VisibilityRule = {
  layer: Layer;
  funnels: FunnelStage[];
};

const columnMatchesHideRule = (
  funnelStage: FunnelStage,
  layer: Layer,
  rules: VisibilityRule[]
): boolean => {
  // Check if any rule matches the current layer and funnel stage
  // The column should be HIDDEN if it matches any rule
  const shouldHide = rules.some(
    (rule) => rule.layer === layer && rule.funnels.includes(funnelStage)
  );
  // Return true if it should be hidden
  return shouldHide;
};

// Helper function to generate filterable and hideable properties based on visibility rules
const shouldHide = (
  funnelStage: FunnelStage,
  layer: Layer,
  rules: VisibilityRule[],
  columnName: string,
  activeColumns: string[]
) => {
  const isHidden = columnMatchesHideRule(funnelStage, layer, rules);
  return {
    filterable: !isHidden,
    hideable: isHidden || !activeColumns.includes(columnName),
  };
};

export const getColumnDefinitions = (
  activeColumns: string[],
  options: {
    currencySymbol: string;
    formatCurrency: (val: number, options?: FormatCurrencyOptions) => string;
    geoView: GeoViewToggle;
    funnelStage: FunnelStage;
    layer: Layer;
    businessUnitToggle?: BusinessUnitToggle;
    metricType?: MetricType;
    hasSOE: boolean;
    config: Config;
  }
) => {
  const {
    formatCurrency,
    geoView,
    currencySymbol,
    funnelStage,
    layer,
    businessUnitToggle = BusinessUnitToggle.AGGREGATE,
    metricType = MetricType.MARKETING_ROI,
    hasSOE,
    config,
  } = options;

  const { revenueLabel, showReturnInsteadOfRevenue } = config;
  const revenueFormatterOptions = showReturnInsteadOfRevenue ? NO_SYMBOL : {};
  const currencyLabelSuffix = showReturnInsteadOfRevenue ? "" : ` (${currencySymbol})`;

  const columns: GridColDef<AggregatedData>[] = [
    {
      field: "channel",
      headerName: "Channels",
      valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => row.channel,
      sortable: true,
      hideable: !activeColumns.includes("channel"),
      type: "string",
    },

    // Geo fields
    ...(geoView === GeoViewToggle.SPLIT
      ? [
          {
            field: "geography",
            headerName: "Geography",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => row.geography,
            hideable: !activeColumns.includes("geography"),
          },
        ]
      : []),

    // Business Unit fields (only when businessUnitToggle is split)
    ...(businessUnitToggle === BusinessUnitToggle.SPLIT
      ? [
          {
            field: "product_hierarchy",
            headerName: "Products",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use type guard to safely check for properties
              const anyRow = row as any;
              if (anyRow.brand && anyRow.category && anyRow.product) {
                return formatProductHierarchy(
                  String(anyRow.brand),
                  String(anyRow.category),
                  String(anyRow.product)
                );
              }
              return "";
            },
            sortable: true,
            hideable: !activeColumns.includes("product_hierarchy"),
          },
        ]
      : []),

    // SOE fields
    ...(hasSOE
      ? [
          {
            field: "brand_marketing_contribution_amount",
            headerName: `Brand Contribution ${currencyLabelSuffix}`,
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row.revenue?.upperFunnelMarketingContribution ?? 0,
            valueFormatter: ({ value }: GridValueFormatterParams) =>
              formatCurrency(value, revenueFormatterOptions),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Portfolio,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Brand,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Product,
                  funnels: [FunnelStage.Category, FunnelStage.Product],
                },
              ],
              "brand_marketing_contribution_amount",
              activeColumns
            ),
          },
          {
            field: "brand_marketing_contribution_percentage",
            headerName: "Brand Contribution (%)",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row.revenuePercentage?.upperFunnelMarketingContribution || 0,
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Portfolio,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Brand,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Product,
                  funnels: [FunnelStage.Category, FunnelStage.Product],
                },
              ],
              "brand_marketing_contribution_percentage",
              activeColumns
            ),
          },
          ...(metricType === MetricType.SALES_UNITS
            ? [
                {
                  field: "brand_marketing_units",
                  headerName: "Brand Contribution (Units)",
                  valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
                    row.units?.upperFunnelMarketingContribution || 0,
                  valueFormatter: ({ value }: GridValueFormatterParams) => formatUnitValue(value),
                  sortable: true,
                  type: "number",
                  ...shouldHide(
                    funnelStage,
                    layer,
                    [
                      {
                        layer: Layer.Portfolio,
                        funnels: [
                          FunnelStage.All,
                          FunnelStage.Brand,
                          FunnelStage.Category,
                          FunnelStage.Product,
                        ],
                      },
                      {
                        layer: Layer.Brand,
                        funnels: [
                          FunnelStage.All,
                          FunnelStage.Brand,
                          FunnelStage.Category,
                          FunnelStage.Product,
                        ],
                      },
                      {
                        layer: Layer.Product,
                        funnels: [FunnelStage.Category, FunnelStage.Product],
                      },
                    ],
                    "brand_marketing_units",
                    activeColumns
                  ),
                },
              ]
            : []),
          {
            field: "category_marketing_contribution_amount",
            headerName: `Category Contribution ${currencyLabelSuffix}`,
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row.revenue?.midFunnelMarketingContribution || 0,
            valueFormatter: ({ value }: GridValueFormatterParams) =>
              formatCurrency(value, revenueFormatterOptions),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Portfolio,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Brand,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Category,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Product,
                  funnels: [FunnelStage.Brand, FunnelStage.Product],
                },
              ],
              "category_marketing_contribution_amount",
              activeColumns
            ),
          },
          {
            field: "category_marketing_contribution_percentage",
            headerName: "Category Contribution (%)",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row.revenuePercentage?.midFunnelMarketingContribution || 0,
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Portfolio,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Brand,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Category,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Product,
                  funnels: [FunnelStage.Brand, FunnelStage.Product],
                },
              ],
              "category_marketing_contribution_percentage",
              activeColumns
            ),
          },
          ...(metricType === MetricType.SALES_UNITS
            ? [
                {
                  field: "category_marketing_units",
                  headerName: "Category Contribution (Units)",
                  valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
                    row.units?.midFunnelMarketingContribution || 0,
                  valueFormatter: ({ value }: GridValueFormatterParams) => formatUnitValue(value),
                  sortable: true,
                  type: "number",
                  ...shouldHide(
                    funnelStage,
                    layer,
                    [
                      {
                        layer: Layer.Portfolio,
                        funnels: [
                          FunnelStage.All,
                          FunnelStage.Brand,
                          FunnelStage.Category,
                          FunnelStage.Product,
                        ],
                      },
                      {
                        layer: Layer.Brand,
                        funnels: [
                          FunnelStage.All,
                          FunnelStage.Brand,
                          FunnelStage.Category,
                          FunnelStage.Product,
                        ],
                      },
                      {
                        layer: Layer.Category,
                        funnels: [
                          FunnelStage.All,
                          FunnelStage.Brand,
                          FunnelStage.Category,
                          FunnelStage.Product,
                        ],
                      },
                      {
                        layer: Layer.Product,
                        funnels: [FunnelStage.Brand, FunnelStage.Product],
                      },
                    ],
                    "category_marketing_units",
                    activeColumns
                  ),
                },
              ]
            : []),
        ]
      : []),

    // Total Spend Percentage
    {
      field: "total_spend_percentage",
      headerName: "Total Spend (%)",
      valueGetter: ({ row }) => row.spendPercentage?.total || 0,
      valueFormatter: ({ value }) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
        ],
        "total_spend_percentage",
        activeColumns
      ),
    },

    // Total Revenue Percentage
    {
      field: "total_revenue_percentage",
      headerName: `Total ${revenueLabel} (%)`,
      valueGetter: ({ row }) => row.revenuePercentage?.total || 0,
      valueFormatter: ({ value }) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
        ],
        "total_revenue_percentage",
        activeColumns
      ),
    },

    // Total ROI Percentage (only shown when metricType is MARKETING_ROI) -- Also don't show at the product layer and when funnel stage is all
    ...(metricType === MetricType.MARKETING_ROI &&
    !(funnelStage === FunnelStage.All && layer === Layer.Product)
      ? [
          {
            field: "total_roi_percentage",
            headerName: "Total ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row?.roi?.total !== null ? row?.roi?.total : 0,
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Portfolio,
                  funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
                },
                {
                  layer: Layer.Brand,
                  funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
                },
                {
                  layer: Layer.Category,
                  funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
                },
                {
                  layer: Layer.Product,
                  funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
                },
              ],
              "total_roi_percentage",
              activeColumns
            ),
          },
        ]
      : []),

    // Total ROAS (only shown when metricType is RETURN_ON_AD_SPEND)
    ...(metricType === MetricType.RETURN_ON_AD_SPEND
      ? [
          {
            field: "total_roas",
            headerName: "Total ROAS",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use roas if available, otherwise fallback to roi
              if ("roas" in row && row.roas && row.roas.total !== undefined) {
                return row.roas.total;
              }
              return row.roi?.total || 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("total_roas"),
          },
        ]
      : []),

    // Total Units (only shown when metricType is SALES_UNITS)
    ...(metricType === MetricType.SALES_UNITS
      ? [
          {
            field: "total_units",
            headerName: "Total Units",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use sampleSize as a fallback since MROI doesn't have units field
              return row.units?.total || 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("total_units"),
          },
        ]
      : []),

    // Spend fields
    {
      field: "total_spend",
      headerName: `Total Spend (${currencySymbol})`,
      valueGetter: ({ row }) => row.spend.total,
      valueFormatter: ({ value }) => formatCurrency(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
        ],
        "total_spend",
        activeColumns
      ),
    },

    // Revenue fields
    {
      field: "total_revenue",
      headerName: `Total ${revenueLabel} ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.revenue.total,
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
        ],
        "total_revenue",
        activeColumns
      ),
    },

    // Yield fields
    {
      field: "total_yield",
      headerName: `Total Yield ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.yield?.total || 0,
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
        ],
        "total_yield",
        activeColumns
      ),
    },

    // Ad Stock Peak
    {
      field: "ad_stock_peak",
      headerName: "Ad Stock Peak",
      valueGetter: ({ row }) => row.adstockPeak || 0,
      valueFormatter: ({ value }) => formatAdStockValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
        ],
        "ad_stock_peak",
        activeColumns
      ),
    },

    // Ad Stock Length
    {
      field: "ad_stock_length",
      headerName: "Ad Stock Length",
      valueGetter: ({ row }) => row.adstockDuration || 0,
      valueFormatter: ({ value }) => formatAdStockValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category, FunnelStage.Product],
          },
        ],
        "ad_stock_length",
        activeColumns
      ),
    },

    // Brand fields
    {
      field: "brand_spend_percentage",
      headerName: "Brand Spend (%)",
      valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
        row.spendPercentage?.[Layer.Brand] || 0,
      valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "brand_spend_percentage",
        activeColumns
      ),
    },
    {
      field: "brand_revenue_percentage",
      headerName: `Brand ${revenueLabel} (%)`,
      valueGetter: ({ row }) => row.revenuePercentage?.[Layer.Brand] || 0,
      valueFormatter: ({ value }) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "brand_revenue_percentage",
        activeColumns
      ),
    },
    {
      field: "brand_spend",
      headerName: `Brand Spend (${currencySymbol})`,
      valueGetter: ({ row }) => row.spend[Layer.Brand],
      valueFormatter: ({ value }) => formatCurrency(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "brand_spend",
        activeColumns
      ),
    },
    {
      field: "brand_revenue",
      headerName: `Brand ${revenueLabel} ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.revenue[Layer.Brand],
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "brand_revenue",
        activeColumns
      ),
    },
    ...(metricType === MetricType.MARKETING_ROI
      ? [
          {
            field: "brand_roi",
            headerName: "Brand ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row?.roi?.[Layer.Brand] !== null ? row?.roi?.[Layer.Brand] : 0,
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Brand,
                  funnels: [FunnelStage.Category, FunnelStage.Product],
                },
                {
                  layer: Layer.Portfolio,
                  funnels: [FunnelStage.Category, FunnelStage.Product],
                },
                {
                  layer: Layer.Category,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
                {
                  layer: Layer.Product,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
              ],
              "brand_roi",
              activeColumns
            ),
          },
        ]
      : []),
    {
      field: "brand_yield",
      headerName: `Brand Yield ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.yield?.[Layer.Brand] || 0,
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Category, FunnelStage.Product],
          },
          {
            layer: Layer.Category,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "brand_yield",
        activeColumns
      ),
    },

    // Brand ROAS - only when metricType is RETURN_ON_AD_SPEND
    ...(metricType === MetricType.RETURN_ON_AD_SPEND
      ? [
          {
            field: "brand_roas",
            headerName: "Brand ROAS",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use roas if available, otherwise fallback to roi
              if ("roas" in row && row.roas && row.roas[Layer.Brand] !== undefined) {
                return row.roas[Layer.Brand];
              }
              return row.roi?.[Layer.Brand] || 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("brand_roas"),
          },
        ]
      : []),

    // Brand Units - only when metricType is SALES_UNITS
    ...(metricType === MetricType.SALES_UNITS
      ? [
          {
            field: "brand_units",
            headerName: "Brand Units",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use units if available, otherwise fallback to appropriate field
              if ("units" in row && row.units && row.units[Layer.Brand] !== undefined) {
                return row.units[Layer.Brand];
              }
              return 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("brand_units"),
          },
        ]
      : []),

    // Category fields
    {
      field: "category_spend_percentage",
      headerName: "Category Spend (%)",
      valueGetter: ({ row }) => row.spendPercentage?.[Layer.Category] || 0,
      valueFormatter: ({ value }) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "category_spend_percentage",
        activeColumns
      ),
    },
    {
      field: "category_revenue_percentage",
      headerName: `Category ${revenueLabel} (%)`,
      valueGetter: ({ row }) => row.revenuePercentage?.[Layer.Category] || 0,
      valueFormatter: ({ value }) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "category_revenue_percentage",
        activeColumns
      ),
    },
    {
      field: "category_spend",
      headerName: `Category Spend (${currencySymbol})`,
      valueGetter: ({ row }) => row.spend[Layer.Category],
      valueFormatter: ({ value }) => formatCurrency(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "category_spend",
        activeColumns
      ),
    },
    {
      field: "category_revenue",
      headerName: `Category ${revenueLabel} ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.revenue[Layer.Category],
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "category_revenue",
        activeColumns
      ),
    },
    ...(metricType === MetricType.MARKETING_ROI
      ? [
          {
            field: "category_roi",
            headerName: "Category ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row?.roi?.[Layer.Category] !== null ? row?.roi?.[Layer.Category] : 0,
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Portfolio,
                  funnels: [FunnelStage.Product, FunnelStage.Brand],
                },
                {
                  layer: Layer.Brand,
                  funnels: [FunnelStage.Product, FunnelStage.Brand],
                },
                {
                  layer: Layer.Category,
                  funnels: [FunnelStage.Product, FunnelStage.Brand],
                },
                {
                  layer: Layer.Product,
                  funnels: [
                    FunnelStage.All,
                    FunnelStage.Brand,
                    FunnelStage.Category,
                    FunnelStage.Product,
                  ],
                },
              ],
              "category_roi",
              activeColumns
            ),
          },
        ]
      : []),
    {
      field: "category_yield",
      headerName: `Category Yield ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.yield?.[Layer.Category] || 0,
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Product, FunnelStage.Brand],
          },
          {
            layer: Layer.Product,
            funnels: [
              FunnelStage.All,
              FunnelStage.Brand,
              FunnelStage.Category,
              FunnelStage.Product,
            ],
          },
        ],
        "category_yield",
        activeColumns
      ),
    },

    // Category ROAS - only when metricType is RETURN_ON_AD_SPEND
    ...(metricType === MetricType.RETURN_ON_AD_SPEND
      ? [
          {
            field: "category_roas",
            headerName: "Category ROAS",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use roas if available, otherwise fallback to roi
              if ("roas" in row && row.roas && row.roas[Layer.Category] !== undefined) {
                return row.roas[Layer.Category];
              }
              return row.roi?.[Layer.Category] || 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("category_roas"),
          },
        ]
      : []),

    // Category Units - only when metricType is SALES_UNITS
    ...(metricType === MetricType.SALES_UNITS
      ? [
          {
            field: "category_units",
            headerName: "Category Units",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use units if available, otherwise fallback to appropriate field
              if ("units" in row && row.units && row.units[Layer.Category] !== undefined) {
                return row.units[Layer.Category];
              }
              return 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("category_units"),
          },
        ]
      : []),

    // Product fields
    {
      field: "product_spend_percentage",
      headerName: "Product Spend (%)",
      valueGetter: ({ row }) => row.spendPercentage?.[Layer.Product] || 0,
      valueFormatter: ({ value }) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
        ],
        "product_spend_percentage",
        activeColumns
      ),
    },
    {
      field: "product_revenue_percentage",
      headerName: `Product ${revenueLabel} (%)`,
      valueGetter: ({ row }) => row.revenuePercentage?.[Layer.Product] || 0,
      valueFormatter: ({ value }) => formatPercentValue(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
        ],
        "product_revenue_percentage",
        activeColumns
      ),
    },
    {
      field: "product_spend",
      headerName: `Product Spend (${currencySymbol})`,
      valueGetter: ({ row }) => row.spend[Layer.Product],
      valueFormatter: ({ value }) => formatCurrency(value),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
        ],
        "product_spend",
        activeColumns
      ),
    },
    {
      field: "product_revenue",
      headerName: `Product ${revenueLabel} ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.revenue[Layer.Product],
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
        ],
        "product_revenue",
        activeColumns
      ),
    },
    ...(metricType === MetricType.MARKETING_ROI
      ? [
          {
            field: "product_roi",
            headerName: "Product ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) =>
              row?.roi?.[Layer.Product] !== null ? row?.roi?.[Layer.Product] : 0,
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            ...shouldHide(
              funnelStage,
              layer,
              [
                {
                  layer: Layer.Portfolio,
                  funnels: [FunnelStage.Brand, FunnelStage.Category],
                },
                {
                  layer: Layer.Brand,
                  funnels: [FunnelStage.Brand, FunnelStage.Category],
                },
                {
                  layer: Layer.Category,
                  funnels: [FunnelStage.Brand, FunnelStage.Category],
                },
                {
                  layer: Layer.Product,
                  funnels: [FunnelStage.Brand, FunnelStage.Category],
                },
              ],
              "product_roi",
              activeColumns
            ),
          },
        ]
      : []),
    {
      field: "product_yield",
      headerName: `Product Yield ${currencyLabelSuffix}`,
      valueGetter: ({ row }) => row.yield?.[Layer.Product] || 0,
      valueFormatter: ({ value }) => formatCurrency(value, revenueFormatterOptions),
      sortable: true,
      type: "number",
      ...shouldHide(
        funnelStage,
        layer,
        [
          {
            layer: Layer.Portfolio,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Brand,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Category,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
          {
            layer: Layer.Product,
            funnels: [FunnelStage.Brand, FunnelStage.Category],
          },
        ],
        "product_yield",
        activeColumns
      ),
    },

    // Product ROAS - only when metricType is RETURN_ON_AD_SPEND
    ...(metricType === MetricType.RETURN_ON_AD_SPEND
      ? [
          {
            field: "product_roas",
            headerName: "Product ROAS",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use roas if available, otherwise fallback to roi
              if ("roas" in row && row.roas && row.roas[Layer.Product] !== undefined) {
                return row.roas[Layer.Product];
              }
              return row.roi?.[Layer.Product] || 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("product_roas"),
          },
        ]
      : []),

    // Product Units - only when metricType is SALES_UNITS
    ...(metricType === MetricType.SALES_UNITS
      ? [
          {
            field: "product_units",
            headerName: "Product Units",
            valueGetter: ({ row }: GridValueGetterParams<AggregatedData>) => {
              // Use units if available, otherwise fallback to appropriate field
              if ("units" in row && row.units && row.units[Layer.Product] !== undefined) {
                return row.units[Layer.Product];
              }
              return 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("product_units"),
          },
        ]
      : []),
  ];

  return columns;
};
